import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../api";
import Topbar from "../Topbar";
import Switch from "../automations/Switch";
import CopyButton from "../booking/CopyButton";
import LandingAnalytics from "./LandingAnalytics";
import "./landingPages.css";

/*
 * INBOUND > LANDING PAGES. The pages lp.remotion.io serves, built from the
 * CRM's existing pieces until a mockup says otherwise: the list, and the page
 * you click opening beside it (never a navigation).
 *
 * What a page is: a slug, and one or more variants, each with every version of
 * its HTML ever saved. Publishing points a variant at a version; rolling back
 * points it at an older one. The numbers are visits -> form submissions ->
 * booked calls, counted by the server (src/landing).
 */

const TABS = [
  ["pages", "Pages"],
  ["analytics", "Analytics"],
];

const STATUS_PILL = { live: "crm-pill--active", draft: "crm-pill--draft", archived: "crm-pill--cancelled" };

const kb = (bytes) => (bytes == null ? "-" : `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`);
const pct = (n) => (n == null ? "-" : `${n}%`);
const when = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

function readFile(file, as = "text") {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    if (as === "base64") reader.readAsDataURL(file);
    else reader.readAsText(file);
  });
}

/* ------------------------------------------------------------ new page -- */

function NewPage({ onCreated, onCancel }) {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const slugFrom = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const page = await api("/landing-pages", { method: "POST", body: JSON.stringify({ title, slug, html }) });
      onCreated(page);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="crm-card crm-lp-new" onSubmit={create}>
      <h3>New landing page</h3>
      <div className="crm-field--row crm-field">
        <label className="crm-field">
          <span className="crm-label">Name</span>
          <input
            className="crm-input"
            value={title}
            onChange={(e) => {
              const next = e.target.value;
              if (!slug || slug === slugFrom(title)) setSlug(slugFrom(next));
              setTitle(next);
            }}
            required
          />
        </label>
        <label className="crm-field">
          <span className="crm-label">Address</span>
          <span className="crm-lp-slug">
            <span className="crm-hint">lp.remotion.io/</span>
            <input className="crm-input" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} required />
          </span>
        </label>
      </div>
      <HtmlInput value={html} onChange={setHtml} />
      {error && <p className="crm-lp-error">{error}</p>}
      <div className="crm-lp-actions">
        <button type="submit" className="crm-btn crm-btn--primary" disabled={busy || !title || !slug}>
          {busy ? "Creating…" : "Create as draft"}
        </button>
        <button type="button" className="crm-btn crm-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/* Paste the HTML, or pick the file. */
function HtmlInput({ value, onChange }) {
  return (
    <label className="crm-field">
      <span className="crm-label">
        HTML{" "}
        <input
          type="file"
          accept=".html,.htm,text/html"
          className="crm-lp-file"
          onChange={async (e) => {
            const file = e.target.files && e.target.files[0];
            if (file) onChange(await readFile(file));
          }}
        />
      </span>
      <textarea
        className="crm-input crm-lp-html"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste the page's HTML, or choose the file above."
        spellCheck={false}
      />
      {value ? <span className="crm-hint">{kb(new Blob([value]).size)}</span> : null}
    </label>
  );
}

/* --------------------------------------------------------- settings -- */

function TrackingSettings({ settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  useEffect(() => setForm(settings), [settings]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const saved = await api("/landing-pages/settings", {
        method: "PUT",
        body: JSON.stringify({
          linkedin_partner_id: form.linkedin_partner_id || "",
          linkedin_conversion_id: form.linkedin_conversion_id || "",
          calendar_url: form.calendar_url || "",
        }),
      });
      onSaved(saved);
      setNote("Saved");
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="crm-card crm-lp-settings" onSubmit={save}>
      <h3>For every page</h3>
      <div className="crm-lp-grid3">
        <label className="crm-field">
          <span className="crm-label">LinkedIn partner ID</span>
          <input className="crm-input" value={form.linkedin_partner_id || ""} onChange={set("linkedin_partner_id")} inputMode="numeric" />
        </label>
        <label className="crm-field">
          <span className="crm-label">LinkedIn conversion ID</span>
          <input
            className="crm-input"
            value={form.linkedin_conversion_id || ""}
            onChange={set("linkedin_conversion_id")}
            inputMode="numeric"
            placeholder="Not set: no conversion fired"
          />
        </label>
        <label className="crm-field">
          <span className="crm-label">Calendar</span>
          <input
            className="crm-input"
            value={form.calendar_url || ""}
            onChange={set("calendar_url")}
            placeholder="The CRM's booking page (crm.remotion.io/book)"
          />
        </label>
      </div>
      <div className="crm-lp-actions">
        <button type="submit" className="crm-btn crm-btn--secondary" disabled={busy}>
          Save
        </button>
        {note && <span className="crm-hint">{note}</span>}
      </div>
    </form>
  );
}

function Assets() {
  const [assets, setAssets] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("/landing-pages/assets").then((d) => setAssets(d.assets.map((a) => ({ ...a, url: `${d.origin}${a.path}` })))), []);
  useEffect(() => {
    load().catch((err) => setError(err.message));
  }, [load]);
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(true);
    setError(null);
    try {
      for (const file of files) {
        const dataUrl = await readFile(file, "base64");
        await api("/landing-pages/assets", {
          method: "POST",
          body: JSON.stringify({ filename: file.name, content_type: file.type, data: String(dataUrl).split(",")[1] }),
        });
      }
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="crm-card crm-lp-assets">
      <div className="crm-lp-sechead">
        <h3>Images and fonts</h3>
        <label className="crm-btn crm-btn--secondary">
          {busy ? "Uploading…" : "Upload"}
          <input type="file" multiple accept="image/*,.woff,.woff2" onChange={upload} hidden />
        </label>
      </div>
      <p className="crm-hint">Served from lp.remotion.io and cached for a year. Use the path in the page's HTML.</p>
      {error && <p className="crm-lp-error">{error}</p>}
      {assets && assets.length ? (
        <ul className="crm-lp-assetlist">
          {assets.map((a) => (
            <li key={a.id}>
              <code>{a.path}</code>
              <span className="crm-hint">{kb(a.bytes)}</span>
              <CopyButton text={a.path} label="Copy path" />
            </li>
          ))}
        </ul>
      ) : assets ? (
        <p className="crm-hint">None yet.</p>
      ) : null}
    </section>
  );
}

/*
 * remotion.io, as its own script reports it (src/site): visits by page and by
 * source, and what became of the form copies - matched to the Webflow webhook,
 * or dropped by Webflow and proposed in Approvals.
 */
function SiteCard() {
  const [s, setS] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api("/landing-pages/site?days=30").then(setS, (err) => setError(err.message));
  }, []);
  if (error) return null;
  return (
    <section className="crm-card crm-lp-site">
      <div className="crm-lp-sechead">
        <h3>remotion.io, last 30 days</h3>
      </div>
      {!s ? (
        <p className="crm-hint">Loading…</p>
      ) : !s.visits && !s.forms.total ? (
        <p className="crm-hint">Nothing yet. The site script reports here once it is on remotion.io.</p>
      ) : (
        <>
          <div className="crm-lp-numbers">
            {[
              ["Visits", s.visits],
              ["Visitors", s.visitors],
              ["From LinkedIn ads", s.linkedin_clicks],
              ["Form copies", s.forms.total],
              ["Webflow dropped", s.forms.dropped_by_webflow],
            ].map(([label, value]) => (
              <div key={label}>
                <b>{value}</b>
                <span className="crm-hint">{label}</span>
              </div>
            ))}
          </div>
          <div className="crm-lp-grid2">
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {s.pages.map((p) => (
                  <tr key={p.path}>
                    <td>{p.path}</td>
                    <td>{p.visits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {s.sources.map((p) => (
                  <tr key={p.source}>
                    <td>{p.source}</td>
                    <td>{p.visits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ detail -- */

function Numbers({ stats }) {
  const t = stats.total;
  return (
    <div className="crm-lp-numbers">
      {[
        ["Visits", t.visits],
        ["Form fills", t.submissions],
        ["Fill rate", pct(t.submission_rate)],
        ["Booked calls", t.bookings],
        ["Visit to call", pct(t.booking_rate)],
      ].map(([label, value]) => (
        <div key={label}>
          <b>{value}</b>
          <span className="crm-hint">{label}</span>
        </div>
      ))}
    </div>
  );
}

function VersionPreview({ versionId, onClose }) {
  const [v, setV] = useState(null);
  useEffect(() => {
    api(`/landing-pages/versions/${versionId}`).then(setV, () => setV({ error: true }));
  }, [versionId]);
  if (!v) return <p className="crm-hint">Loading…</p>;
  if (v.error) return <p className="crm-lp-error">Couldn't load that version.</p>;
  /*
   * SANDBOXED, WITH NO SAME-ORIGIN. The HTML is a page nobody in the CRM wrote
   * and may run scripts; without the sandbox those scripts would run on the
   * CRM's own origin, next to the login token. Forms cannot submit from here.
   */
  const base = `<base href="${v.origin}/">`;
  const doc = /<head[^>]*>/i.test(v.html) ? v.html.replace(/<head[^>]*>/i, (m) => `${m}${base}`) : base + v.html;
  return (
    <div className="crm-lp-preview">
      <div className="crm-lp-sechead">
        <span className="crm-hint">
          Version {v.id} · {kb(v.lint.code_bytes)} of HTML, CSS and JS (budget 100 KB)
          {v.lint.inline_asset_bytes ? ` + ${kb(v.lint.inline_asset_bytes)} of embedded images` : ""}
        </span>
        <button type="button" className="crm-peek__close" onClick={onClose} aria-label="Close preview">
          ×
        </button>
      </div>
      {v.lint.notes.length ? (
        <ul className="crm-lp-lint">
          {v.lint.notes.map((n) => (
            <li key={n.text} className={`is-${n.level}`}>
              {n.text}
            </li>
          ))}
        </ul>
      ) : null}
      <iframe title={`Version ${v.id}`} sandbox="allow-scripts" srcDoc={doc} className="crm-lp-frame" />
    </div>
  );
}

function Variant({ page, variant, onChanged }) {
  const [versions, setVersions] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [html, setHtml] = useState("");
  const [adding, setAdding] = useState(false);
  const [weight, setWeight] = useState(variant.weight);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => setWeight(variant.weight), [variant.weight]);

  const loadVersions = useCallback(
    () => api(`/landing-pages/variants/${variant.id}/versions`).then((d) => setVersions(d.versions)),
    [variant.id]
  );
  useEffect(() => {
    if (open) loadVersions().catch((err) => setError(err.message));
  }, [open, loadVersions]);

  const act = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await Promise.all([onChanged(), open ? loadVersions() : null]);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const publish = (versionId) =>
    act(() => api(`/landing-pages/variants/${variant.id}/publish`, { method: "POST", body: JSON.stringify({ version_id: versionId }) }));
  const save = () =>
    act(async () => {
      const v = await api(`/landing-pages/variants/${variant.id}/versions`, { method: "POST", body: JSON.stringify({ html }) });
      setHtml("");
      setAdding(false);
      setOpen(true);
      setViewing(v.id);
    });
  const saveWeight = () =>
    act(() => api(`/landing-pages/variants/${variant.id}`, { method: "PATCH", body: JSON.stringify({ weight: Number(weight) }) }));

  // A variant that is another address (redirect_url) is in the split without a version.
  const servable = (v) => v.live_version_id || v.redirect_url;
  const totalWeight = page.variants.reduce((n, v) => n + (v.weight > 0 && servable(v) ? v.weight : 0), 0);
  const share = variant.weight > 0 && servable(variant) && totalWeight ? Math.round((variant.weight / totalWeight) * 100) : 0;

  return (
    <div className="crm-lp-variant">
      <div className="crm-lp-sechead">
        <div>
          <b>
            {variant.key.toUpperCase()} · {variant.label || "Variant"}
          </b>
          <div className="crm-hint">
            {variant.redirect_url
              ? `Sends visitors to ${variant.redirect_url}, with their ad tags`
              : variant.live_version_id
                ? `Live: version ${variant.live_version_id}, ${kb(variant.live_bytes)}`
                : "Nothing published"}
            {" · "}
            {share ? `${share}% of visitors` : "not in the split"}
          </div>
        </div>
        <a className="crm-linkact" href={variant.preview_url} target="_blank" rel="noreferrer">
          Open preview ↗
        </a>
      </div>

      <div className="crm-lp-weight">
        <label className="crm-hint" htmlFor={`w-${variant.id}`}>
          Weight
        </label>
        <input
          id={`w-${variant.id}`}
          className="crm-input"
          type="number"
          min="0"
          max="1000"
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
        />
        {String(weight) !== String(variant.weight) && (
          <button type="button" className="crm-btn crm-btn--secondary" onClick={saveWeight} disabled={busy}>
            Save
          </button>
        )}
        <span className="crm-hint">0 keeps it out of the split (preview only).</span>
      </div>

      {!variant.redirect_url && (
        <div className="crm-lp-actions">
          <button type="button" className="crm-linkact" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide versions" : "Versions"}
          </button>
          <button type="button" className="crm-linkact" onClick={() => setAdding((a) => !a)}>
            {adding ? "Cancel" : "Upload a new version"}
          </button>
        </div>
      )}

      {adding && (
        <div className="crm-lp-upload">
          <HtmlInput value={html} onChange={setHtml} />
          <button type="button" className="crm-btn crm-btn--secondary" onClick={save} disabled={busy || !html.trim()}>
            Save version (not published yet)
          </button>
        </div>
      )}

      {error && <p className="crm-lp-error">{error}</p>}

      {open && versions && (
        <table className="crm-table crm-lp-versions">
          <tbody>
            {versions.map((v) => {
              const live = v.id === variant.live_version_id;
              const older = variant.live_version_id && v.id < variant.live_version_id;
              return (
                <tr key={v.id} className={live ? "crm-tr--selected" : ""}>
                  <td>
                    <b>v{v.id}</b> <span className="crm-hint">{when(v.created_at)}</span>
                    {v.note ? <div className="crm-hint">{v.note}</div> : null}
                  </td>
                  <td className="crm-hint">{kb(v.bytes)}</td>
                  <td className="crm-lp-vact">
                    <button type="button" className="crm-linkact" onClick={() => setViewing(viewing === v.id ? null : v.id)}>
                      {viewing === v.id ? "Hide" : "View"}
                    </button>
                    {live ? (
                      <span className="crm-pill crm-pill--active">Live</span>
                    ) : (
                      <button type="button" className="crm-btn crm-btn--secondary" disabled={busy} onClick={() => publish(v.id)}>
                        {older ? "Roll back to this" : "Publish"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {viewing && <VersionPreview versionId={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function AddVariant({ page, onAdded }) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [from, setFrom] = useState(page.variants[0] ? String(page.variants[0].id) : "");
  const [sendTo, setSendTo] = useState("");
  const [error, setError] = useState(null);
  const elsewhere = from === "url";
  const add = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await api(`/landing-pages/${page.id}/variants`, {
        method: "POST",
        body: JSON.stringify(
          elsewhere
            ? { key, label, weight: 0, redirect_url: sendTo }
            : { key, label, weight: 0, from_variant_id: from && from !== "url" ? Number(from) : null }
        ),
      });
      setKey("");
      setLabel("");
      setSendTo("");
      onAdded();
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <form className="crm-lp-addvariant" onSubmit={add}>
      <input className="crm-input" placeholder="Key, e.g. c" value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} maxLength={12} required />
      <input className="crm-input" placeholder="Name" value={label} onChange={(e) => setLabel(e.target.value)} />
      <select className="crm-select" value={from} onChange={(e) => setFrom(e.target.value)}>
        <option value="">Start empty</option>
        {page.variants.map((v) => (
          <option key={v.id} value={v.id}>
            Copy {v.key.toUpperCase()}
          </option>
        ))}
        <option value="url">Send to another address</option>
      </select>
      {elsewhere && (
        <input className="crm-input" type="url" placeholder="https://www.remotion.io/" value={sendTo} onChange={(e) => setSendTo(e.target.value)} required />
      )}
      <button type="submit" className="crm-btn crm-btn--secondary">
        Add variant
      </button>
      {error && <p className="crm-lp-error">{error}</p>}
    </form>
  );
}

function PageSettings({ page, onSaved }) {
  const [calendar, setCalendar] = useState(page.calendar_url || "");
  const [pending, setPending] = useState({});
  const [error, setError] = useState(null);
  useEffect(() => setCalendar(page.calendar_url || ""), [page.calendar_url]);
  const patch = async (field, value) => {
    setPending((p) => ({ ...p, [field]: true }));
    setError(null);
    try {
      await api(`/landing-pages/${page.id}`, { method: "PATCH", body: JSON.stringify({ [field]: value }) });
      await onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setPending((p) => ({ ...p, [field]: false }));
    }
  };
  return (
    <div className="crm-lp-pagesettings">
      <div className="crm-auto-switch">
        <Switch on={!page.noindex} pending={pending.noindex} onChange={(on) => patch("noindex", !on)} label="Let search engines index this page" />
        <span>Search engines may index it</span>
      </div>
      <div className="crm-auto-switch">
        <Switch on={page.auto_reply} pending={pending.auto_reply} onChange={(on) => patch("auto_reply", on)} label="After-hours auto-reply" />
        <span>Send the after-hours reply to its leads</span>
      </div>
      <label className="crm-field">
        <span className="crm-label">Calendar for this page</span>
        <span className="crm-lp-inline">
          <input className="crm-input" value={calendar} onChange={(e) => setCalendar(e.target.value)} placeholder={page.calendar_effective} />
          {calendar !== (page.calendar_url || "") && (
            <button type="button" className="crm-btn crm-btn--secondary" onClick={() => patch("calendar_url", calendar)} disabled={pending.calendar_url}>
              Save
            </button>
          )}
        </span>
        <span className="crm-hint">
          {page.calendar_handoff
            ? "The CRM's booking page: name and email are filled in, and the booking counts against this page."
            : "Another calendar: no prefill, and bookings there are not counted here."}
        </span>
      </label>
      {error && <p className="crm-lp-error">{error}</p>}
    </div>
  );
}

function Detail({ id, onClose, onListChanged }) {
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => api(`/landing-pages/${id}`).then(setPage), [id]);
  useEffect(() => {
    setPage(null);
    load().catch((err) => setError(err.message));
  }, [load]);
  const refresh = async () => {
    await load();
    onListChanged();
  };
  const setStatus = async (status) => {
    setError(null);
    try {
      await api(`/landing-pages/${id}/status`, { method: "POST", body: JSON.stringify({ status }) });
      await refresh();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!page) return <aside className="crm-card crm-lp-panel">{error ? <p className="crm-lp-error">{error}</p> : <p className="crm-hint">Loading…</p>}</aside>;
  return (
    <aside className="crm-card crm-lp-panel">
      <div className="crm-lp-panel__head">
        <div>
          <h3>{page.title}</h3>
          <span className={`crm-pill ${STATUS_PILL[page.status]}`}>{page.status}</span>{" "}
          <a className="crm-linkact" href={page.status === "live" ? page.url : page.preview_url} target="_blank" rel="noreferrer">
            {page.url.replace(/^https:\/\//, "")} ↗
          </a>
        </div>
        <button type="button" className="crm-peek__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <div className="crm-lp-actions">
        {page.status !== "live" && (
          <button type="button" className="crm-btn crm-btn--primary" onClick={() => setStatus("live")}>
            {page.status === "archived" ? "Restore and go live" : "Go live"}
          </button>
        )}
        {page.status === "live" && (
          <button type="button" className="crm-btn crm-btn--secondary" onClick={() => setStatus("draft")}>
            Take offline (draft)
          </button>
        )}
        {page.status !== "archived" && (
          <button type="button" className="crm-btn crm-btn--ghost" onClick={() => setStatus("archived")}>
            Archive
          </button>
        )}
        <CopyButton text={page.preview_url} label="Copy preview link" />
      </div>
      {error && <p className="crm-lp-error">{error}</p>}

      <div className="crm-lp-sec">
        <Numbers stats={page.stats} />
        {page.stats.variants.length > 1 && (
          <table className="crm-table crm-lp-bytable">
            <thead>
              <tr>
                <th>Variant</th>
                <th>Visits</th>
                <th>Fills</th>
                <th>Rate</th>
                <th>Calls</th>
              </tr>
            </thead>
            <tbody>
              {page.stats.variants.map((v) => (
                <tr key={v.variant_id}>
                  <td>
                    {v.key.toUpperCase()} <span className="crm-hint">{v.label}</span>
                    {v.redirect_url && <span className="crm-hint"> → {new URL(v.redirect_url).hostname.replace(/^www\./, "")}</span>}
                  </td>
                  <td>{v.visits}</td>
                  <td>{v.submissions}</td>
                  <td>{pct(v.submission_rate)}</td>
                  <td>{v.bookings}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="crm-hint">Counted by the server; previews never count. A call counts when it was booked from the page, or by the same person within two weeks. A variant that sends visitors elsewhere counts there: their remotion.io visits, the forms they sent and the calls those booked.</p>
      </div>

      <div className="crm-lp-sec">
        <h4>Variants</h4>
        {page.variants.map((v) => (
          <Variant key={v.id} page={page} variant={v} onChanged={refresh} />
        ))}
        <AddVariant page={page} onAdded={refresh} />
      </div>

      <div className="crm-lp-sec">
        <h4>Settings</h4>
        <PageSettings page={page} onSaved={refresh} />
      </div>

      <div className="crm-lp-sec">
        <h4>Latest form fills</h4>
        {page.submissions.length ? (
          <ul className="crm-lp-subs">
            {page.submissions.map((s) => (
              <li key={s.id}>
                <div>
                  {s.contact_id ? <Link to={`/contacts/${s.contact_id}`}>{s.name}</Link> : s.name} <span className="crm-hint">{s.email}</span>
                  {s.preview ? <span className="crm-pill crm-pill--draft">test</span> : null}
                </div>
                <div className="crm-hint">
                  {when(s.created_at)} · {s.variant ? s.variant.toUpperCase() : "-"}
                  {s.utm_source ? ` · ${s.utm_source}` : ""}
                  {s.utm_campaign ? ` / ${s.utm_campaign}` : ""}
                  {s.has_click_id ? " · LinkedIn click" : ""}
                  {s.booking_id ? ` · booked ${when(s.booking_starts_at)}` : " · not booked"}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="crm-hint">None yet.</p>
        )}
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------- page -- */

export default function LandingPagesPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "analytics" ? "analytics" : "pages";
  const load = useCallback(() => api("/landing-pages").then(setData), []);
  useEffect(() => {
    load().catch((err) => setError(err.message));
  }, [load]);

  if (error) {
    return (
      <>
        <Topbar title="Landing pages" measured />
        <div className="crm-auto">
          <div className="crm-card crm-card--warning">{error}</div>
        </div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <Topbar title="Landing pages" measured />
        <div className="crm-auto">
          <p className="crm-hint">Loading…</p>
        </div>
      </>
    );
  }
  const live = data.pages.filter((p) => p.status === "live").length;
  return (
    <>
      <Topbar title="Landing pages" sub={`${live} live · ${data.origin.replace(/^https:\/\//, "")}`} measured>
        {tab === "pages" && (
          <button type="button" className="crm-btn crm-btn--primary" onClick={() => setCreating(true)}>
            New page
          </button>
        )}
      </Topbar>
      <div className="crm-auto">
        <div className="crm-auto-tabs crm-lp-tabs" role="tablist">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`crm-auto-tab${tab === key ? " is-active" : ""}`}
              onClick={() => setParams(key === "pages" ? {} : { tab: key }, { replace: true })}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "analytics" ? (
          <LandingAnalytics pages={data.pages} />
        ) : (
          <>
            {creating && (
              <NewPage
                onCancel={() => setCreating(false)}
                onCreated={(page) => {
                  setCreating(false);
                  load();
                  setOpen(page.id);
                }}
              />
            )}
            <div className={`crm-lp-layout${open ? " has-panel" : ""}`}>
              <div className="crm-lp-main">
                <div className="crm-card crm-lp-tablecard">
                  <table className="crm-table">
                    <thead>
                      <tr>
                        <th>Page</th>
                        <th>Status</th>
                        <th>Visits</th>
                        <th>Fills</th>
                        <th>Rate</th>
                        <th>Calls</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pages.map((p) => (
                        <tr key={p.id} className={`crm-tr--link${open === p.id ? " crm-tr--open" : ""}`} onClick={() => setOpen(open === p.id ? null : p.id)}>
                          <td>
                            <b>{p.title}</b>
                            <div className="crm-hint">/{p.slug}</div>
                          </td>
                          <td>
                            <span className={`crm-pill ${STATUS_PILL[p.status]}`}>{p.status}</span>
                          </td>
                          <td>{p.stats.visits}</td>
                          <td>{p.stats.submissions}</td>
                          <td>{pct(p.stats.submission_rate)}</td>
                          <td>{p.stats.bookings}</td>
                          <td className="crm-hint">{when(p.updated_at)}</td>
                        </tr>
                      ))}
                      {!data.pages.length && (
                        <tr>
                          <td colSpan={7} className="crm-empty">
                            No pages yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <SiteCard />
                <TrackingSettings settings={data.settings} onSaved={(s) => setData((d) => ({ ...d, settings: s }))} />
                <Assets />
              </div>
              {open && <Detail key={open} id={open} onClose={() => setOpen(null)} onListChanged={() => load()} />}
            </div>
          </>
        )}
      </div>
    </>
  );
}
